"use client"

import styled from "@emotion/styled"
import MuiCheckbox from "@mui/material/Checkbox"
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form"
import { getNonUmbracoUrl } from "utils"
import CheckboxIcon from "../../../icons/CheckboxIcon"
import ActiveCheckbox from "../../../icons/ActiveCheckbox"
import { colors } from "../../../theme/colors"
import { sizes } from "../../../theme/sizes"
import { StyledRequired, StyledFieldError } from "../../forms/FormShell.styles"

const StyledCheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

const StyledCheckboxWrapper = styled.div`
  flex-shrink: 0;

  .MuiCheckbox-root {
    padding: 0;
    width: 24px;
    height: 24px;

    &:hover {
      background-color: transparent;
    }
  }
`

const StyledCheckboxLabel = styled.span`
  flex: 1;
  color: ${colors.primitives.neutral600};
  font-size: 16px;
  line-height: 24px;

  & a {
    text-decoration: underline;
    color: inherit;
  }

  /* The consent text is a single rich-text paragraph. Render it inline so the
     required asterisk sits at the end of the sentence instead of dropping to
     its own line. */
  & p {
    margin: 0;
    display: inline;
  }
`

/* Required asterisk that follows the consent sentence inline (small gap so it
   doesn't glue to the last word). */
const StyledCheckboxRequired = styled(StyledRequired)`
  margin-inline-start: 4px;
`

/* The shared field error carries a 16px pill inset meant for text inputs.
   Under the checkbox the message must line up with the label text start,
   so drop the horizontal inset here. */
const StyledCheckboxError = styled(StyledFieldError)`
  padding-inline: 0;
`

/**
 * Umbraco rich-text local links arrive with the content-tree prefix
 * (e.g. `/pages/terms-of-use`). The site serves those pages from the root,
 * so strip the `/pages` segment from every href — without touching styling
 * (link colour stays `inherit`, per the label CSS). Covers all forms.
 */
function fixLocalLinks(html: string): string {
  return html.replace(
    /(<a\s+[^>]*href=["'])([^"'>]+)(["'])/gi,
    (_match, before, href, after) =>
      `${before}${getNonUmbracoUrl(href, true)}${after}`,
  )
}

interface FormCheckboxFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  /** Rich-text markup from Umbraco (rendered as the label). */
  markup?: string | null
  required?: boolean
  error?: string
}

export default function FormCheckboxField<T extends FieldValues>({
  control,
  name,
  markup,
  required,
  error,
}: FormCheckboxFieldProps<T>) {
  return (
    <StyledCheckboxRow>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <StyledCheckboxWrapper>
            <MuiCheckbox
              checked={!!field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              icon={<CheckboxIcon />}
              checkedIcon={<ActiveCheckbox />}
              disableRipple
            />
          </StyledCheckboxWrapper>
        )}
      />
      <div>
        {markup ? (
          <StyledCheckboxLabel
            dangerouslySetInnerHTML={{ __html: fixLocalLinks(markup) }}
          />
        ) : null}
        {required && <StyledCheckboxRequired>*</StyledCheckboxRequired>}
        {error && <StyledCheckboxError>{error}</StyledCheckboxError>}
      </div>
    </StyledCheckboxRow>
  )
}
